// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 5 — "The register": a zoomable ICICLE over system → award → division.
//
// An icicle, not a treemap, because depth is the point. The register is a real
// three-level hierarchy and an icicle puts depth on an axis, so a reader can see
// at a glance that one column-2 block (the National Medal) is bigger than five
// whole systems put together — and can then be told, in the same breath, that it
// is a fifteen-year LONG-SERVICE medal rather than an achievement award.
//
// The layout is a pure function (`icicleLayout`) so its correctness can be
// asserted without a DOM: children never escape their parent, siblings are
// flush, nothing leaves the canvas and no coordinate is NaN.

import { escapeHtml, num, pct, tipAttr } from '../format';
import { gloss } from '../glossary';
import { systemColour } from '../palette';
import { attachSvgZoom, type SvgZoomHandle } from '../svgZoom';
import type { View } from '../types';

/* ------------------------------------------------------------------ layout */

export interface IcicleInput {
  name: string;
  n: number;
  children?: IcicleInput[];
}

export interface IcicleRect {
  name: string;
  n: number;
  /** 0 = the focused node itself, 1 = its children, 2 = its grandchildren. */
  depth: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Child indices from the focused node. Empty for the focus itself. */
  path: number[];
  /** The `n` of this rect's parent — the denominator for "share of parent". */
  parentN: number;
  hasChildren: boolean;
}

export interface IcicleOpts {
  width: number;
  height: number;
  /** How many generations below the focus to draw. */
  maxDepth: number;
  /** Width of the leading column that draws the focused node itself. */
  rootWidth: number;
}

const val = (x: number): number => (Number.isFinite(x) && x > 0 ? x : 0);

/**
 * Horizontal icicle: x is depth, y is proportional extent.
 *
 * Siblings are laid out by CUMULATIVE fraction rather than by running offset, so
 * the last child's edge lands exactly on the parent's edge and adjacent
 * siblings share an edge exactly — no gaps, no overlap, no drift.
 */
export function icicleLayout(root: IcicleInput, opts: IcicleOpts): IcicleRect[] {
  const out: IcicleRect[] = [];
  const width = val(opts.width);
  const height = val(opts.height);
  if (width <= 0 || height <= 0) return out;

  const maxDepth = Number.isFinite(opts.maxDepth) ? Math.max(0, Math.floor(opts.maxDepth)) : 0;
  const rootWidth = Math.min(Math.max(0, val(opts.rootWidth)), width);
  const colW = maxDepth > 0 ? (width - rootWidth) / maxDepth : 0;

  out.push({
    name: root.name,
    n: root.n,
    depth: 0,
    x: 0,
    y: 0,
    w: rootWidth,
    h: height,
    path: [],
    parentN: root.n,
    hasChildren: (root.children?.length ?? 0) > 0,
  });

  const walk = (node: IcicleInput, depth: number, y0: number, h: number, path: number[]): void => {
    const kids = node.children;
    if (!kids || kids.length === 0 || depth > maxDepth || h <= 0) return;

    let total = 0;
    for (const k of kids) total += val(k.n);

    const x = rootWidth + (depth - 1) * colW;
    let acc = 0;
    for (let i = 0; i < kids.length; i++) {
      const k = kids[i];
      const v = val(k.n);
      // Equal split is the honest fallback when every child is zero: it keeps
      // the cells flush and finite rather than collapsing them onto one edge.
      const f0 = total > 0 ? acc / total : i / kids.length;
      acc += v;
      const f1 = total > 0 ? acc / total : (i + 1) / kids.length;
      const yTop = y0 + h * f0;
      const yBot = y0 + h * f1;
      const kidPath = path.concat(i);
      out.push({
        name: k.name,
        n: k.n,
        depth,
        x,
        y: yTop,
        w: colW,
        h: Math.max(0, yBot - yTop),
        path: kidPath,
        parentN: node.n,
        hasChildren: (k.children?.length ?? 0) > 0,
      });
      if (depth < maxDepth) walk(k, depth + 1, yTop, yBot - yTop, kidPath);
    }
  };

  walk(root, 1, 0, height, []);
  return out;
}

/* ------------------------------------------------------------- tree helpers */

function nodeAt(root: IcicleInput, path: number[]): IcicleInput | null {
  let node: IcicleInput = root;
  for (const i of path) {
    const kids = node.children;
    if (!kids || i < 0 || i >= kids.length) return null;
    node = kids[i];
  }
  return node;
}

function depthBelow(node: IcicleInput): number {
  const kids = node.children;
  if (!kids || kids.length === 0) return 0;
  let d = 0;
  for (const k of kids) d = Math.max(d, depthBelow(k));
  return d + 1;
}

/** Does this subtree contain a leaf (a division) with this name? */
function containsLeaf(node: IcicleInput, name: string): boolean {
  const kids = node.children;
  if (!kids || kids.length === 0) return node.name === name;
  for (const k of kids) if (containsLeaf(k, name)) return true;
  return false;
}

function leafTotal(node: IcicleInput, name: string): number {
  const kids = node.children;
  if (!kids || kids.length === 0) return node.name === name ? val(node.n) : 0;
  let t = 0;
  for (const k of kids) t += leafTotal(k, name);
  return t;
}

/* ------------------------------------------------------------------ colour */

const PAPER: [number, number, number] = [251, 250, 247];

/** Mix a palette colour toward paper. Depth desaturates; hue is never invented. */
function tint(hex: string, k: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const v = parseInt(m[1], 16);
  const ch: [number, number, number] = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  const t = Math.min(1, Math.max(0, Number.isFinite(k) ? k : 0));
  const mixed = ch.map((c, i) => Math.round(c + (PAPER[i] - c) * t));
  return `#${mixed.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/* ------------------------------------------------------------------- utils */

const safe = (v: number): number => (Number.isFinite(v) ? Math.round(v * 100) / 100 : 0);

function fitText(s: string, wpx: number, fs: number): string {
  const max = Math.floor((wpx - 10) / (fs * 0.6));
  if (!Number.isFinite(max) || max < 4) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

const LEVELS = ['The register', 'System', 'Award', 'Division'];

const STYLE = `<style>
.reg-crumbs{display:flex;flex-wrap:wrap;align-items:center;gap:.25rem .35rem;margin-bottom:var(--space-md);font-size:var(--font-size-sm)}
.reg-crumbs button{font:inherit;font-size:var(--font-size-sm);min-height:40px;padding:.25rem .6rem;border:1px solid var(--border-default);
  background:var(--bg-surface);color:var(--accent-primary-hover);border-radius:var(--radius-sm);cursor:pointer;
  max-width:min(320px,100%);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.reg-crumbs button:hover{background:var(--accent-wash)}
.reg-crumbs .reg-here{display:inline-flex;align-items:center;min-height:40px;padding:.25rem .6rem;font-weight:600;color:var(--text-primary);
  max-width:min(320px,100%);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.reg-crumbs .reg-sep{color:var(--text-muted);flex-shrink:0}
.reg-chart{position:relative;margin-bottom:var(--space-sm)}
.reg-node{cursor:default}
.reg-node.reg-act{cursor:pointer}
.reg-node.reg-act:hover{stroke:var(--text-primary);stroke-width:1.2}
.reg-node:focus-visible{outline:2px solid var(--accent-secondary);outline-offset:1px}
.reg-legend{display:flex;flex-wrap:wrap;gap:var(--space-sm) var(--space-md);margin-top:var(--space-md);font-size:var(--font-size-sm)}
.reg-legend button{display:inline-flex;align-items:center;gap:var(--space-xs);min-height:40px;min-width:0;max-width:100%;
  font:inherit;font-size:var(--font-size-sm);padding:.25rem .6rem;border:1px solid var(--border-default);border-radius:999px;
  background:var(--bg-surface);color:var(--text-primary);cursor:pointer}
.reg-legend button:hover{background:var(--accent-wash)}
.reg-legend button[aria-current='true']{border-color:var(--accent-secondary);box-shadow:inset 0 0 0 1px var(--accent-secondary)}
.reg-legend .reg-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.reg-legend .reg-n{flex-shrink:0;font-family:var(--font-mono);font-variant-numeric:tabular-nums;color:var(--text-secondary)}
.reg-detail{border-top:1px solid var(--border-subtle);margin-top:var(--space-lg);padding-top:var(--space-md)}
.reg-divpills{display:flex;flex-wrap:wrap;gap:var(--space-sm);margin-top:var(--space-sm)}
.reg-divpills button{display:inline-flex;align-items:center;gap:var(--space-xs);min-height:40px;min-width:0;max-width:100%;
  font:inherit;font-size:var(--font-size-sm);padding:.25rem .7rem;border:1px solid var(--border-default);border-radius:999px;
  background:var(--accent-wash);color:var(--text-primary);cursor:pointer}
.reg-divpills button[aria-pressed='true']{background:var(--accent-secondary);border-color:var(--accent-secondary);color:#fff}
.reg-divpills .reg-n{flex-shrink:0;font-family:var(--font-mono);font-variant-numeric:tabular-nums}
.reg-axis{margin-top:var(--space-xs)}
</style>`;

/* -------------------------------------------------------------------- view */

export const registerView: View = (root, ctx) => {
  const core = ctx.core;
  const TOTAL = val(core.totals.records);

  const tree: IcicleInput = {
    name: 'the whole register',
    n: core.totals.records,
    children: core.hierarchy as IcicleInput[],
  };

  // --- state from the URL -------------------------------------------------
  const params = new URLSearchParams(root.dataset.params ?? '');
  const raw = params.get('p') ?? '';
  const focusPath: number[] = [];
  if (raw) {
    let cursor: IcicleInput = tree;
    for (const part of raw.split('.')) {
      const i = Number.parseInt(part, 10);
      const kids = cursor.children;
      if (!Number.isInteger(i) || !kids || i < 0 || i >= kids.length) break;
      // Never focus a leaf: there would be nothing below it to draw.
      if ((kids[i].children?.length ?? 0) === 0) break;
      focusPath.push(i);
      cursor = kids[i];
    }
  }
  const focusNode = nodeAt(tree, focusPath) ?? tree;
  const focusDepth = focusPath.length;

  // --- register-wide division tallies -------------------------------------
  const divTotals = new Map<string, number>();
  for (const sys of tree.children ?? []) {
    for (const award of sys.children ?? []) {
      for (const div of award.children ?? []) {
        divTotals.set(div.name, (divTotals.get(div.name) ?? 0) + val(div.n));
      }
    }
  }
  const divisions = [...divTotals.entries()].sort((a, b) => b[1] - a[1]);

  const nationalMedal = (tree.children ?? []).reduce(
    (t, sys) =>
      t + (sys.children ?? []).filter((a) => a.name === 'National Medal').reduce((s, a) => s + val(a.n), 0),
    0,
  );
  const oa = (tree.children ?? []).find((s) => s.name === 'Order of Australia');
  const oaN = val(oa?.n ?? 0);

  let selected: string | null = null;

  // --- static shell -------------------------------------------------------
  const crumbs: string[] = [];
  for (let i = 0; i <= focusPath.length; i++) {
    const node = nodeAt(tree, focusPath.slice(0, i));
    if (!node) continue;
    const label = i === 0 ? 'The whole register' : node.name;
    const target = focusPath.slice(0, i).join('.');
    if (i === focusPath.length) {
      crumbs.push(
        `<span class="reg-here" aria-current="true" data-tip="${tipAttr(`${label}\n${num(val(node.n))} records — ${pct(TOTAL > 0 ? val(node.n) / TOTAL : NaN)} of the register`)}">${escapeHtml(label)}</span>`,
      );
    } else {
      crumbs.push(
        `<button type="button" data-goto="${escapeHtml(target)}" data-tip="${tipAttr(`Zoom back out to ${label} — ${num(val(node.n))} records`)}">${escapeHtml(label)}</button>`,
        `<span class="reg-sep" aria-hidden="true">›</span>`,
      );
    }
  }

  const legend = (tree.children ?? [])
    .map((sys, i) => {
      const share = TOTAL > 0 ? val(sys.n) / TOTAL : NaN;
      const here = focusPath.length > 0 && focusPath[0] === i;
      return `<button type="button" data-goto="${i}" aria-current="${here ? 'true' : 'false'}"
        aria-label="Zoom into ${escapeHtml(sys.name)} awards, ${num(val(sys.n))} records"
        data-tip="${tipAttr(`${sys.name}\n${num(val(sys.n))} records — ${pct(share)} of the register\nClick to zoom into this system`)}">
        <span class="swatch" style="background:${systemColour(sys.name)}"></span>
        <span class="reg-name">${escapeHtml(sys.name)}</span>
        <span class="reg-n">${num(val(sys.n))}</span></button>`;
    })
    .join('');

  const flat: { sys: string; sysIdx: number; awardIdx: number; name: string; n: number }[] = [];
  (tree.children ?? []).forEach((sys, si) => {
    (sys.children ?? []).forEach((a, ai) => {
      flat.push({ sys: sys.name, sysIdx: si, awardIdx: ai, name: a.name, n: val(a.n) });
    });
  });
  flat.sort((a, b) => b.n - a.n);
  const tableRows = flat
    .slice(0, 20)
    .map((r) => {
      const share = TOTAL > 0 ? r.n / TOTAL : NaN;
      return `<tr data-row data-goto="${r.sysIdx}.${r.awardIdx}"
        data-tip="${tipAttr(`${r.name}\n${r.sys} system\n${num(r.n)} records — ${pct(share)} of the register\nClick to zoom into this award`)}">
        <td>${escapeHtml(r.name)}</td>
        <td><span class="swatch" style="background:${systemColour(r.sys)};display:inline-block;vertical-align:-2px"></span> ${escapeHtml(r.sys)}</td>
        <td class="n">${num(r.n)}</td><td class="n">${pct(share)}</td></tr>`;
    })
    .join('');

  root.innerHTML = `${STYLE}
    <div class="view-head">
      <h1>The register</h1>
      <p class="lede">Every one of the ${num(TOTAL)} awards on the public register, arranged by the
        three levels the register itself uses: the award system, the award, and — where it is recorded
        at all — the ${gloss('division', 'division')}. Each block's height is its share of the block
        to its left.</p>
    </div>

    <div class="note">
      <p><strong>Two records in every five here are the same medal.</strong> The
        ${gloss('national-medal', 'National Medal')} accounts for ${num(nationalMedal)} records —
        ${pct(TOTAL > 0 ? nationalMedal / TOTAL : NaN)} of the whole register — and it is awarded for
        <strong>15 years of diligent service</strong> in the police, fire, ambulance, emergency,
        corrective and volunteer services. It measures the length of a career, not a particular
        achievement. The largest block below is therefore the most <em>common</em> honour in Australia,
        and reading it as the most honoured achievement would be a mistake.</p>
      <p style="margin-top:.5rem">The ${gloss('order', 'Order of Australia')} — the AC, AO, AM and
        ${gloss('oam', 'OAM')} that most people picture when they hear “an honour” — is
        ${num(oaN)} records, ${pct(TOTAL > 0 ? oaN / TOTAL : NaN)} of the register.</p>
    </div>

    <div class="stats">
      <div class="stat" data-tip="${tipAttr(`${num(TOTAL)} award records on the register at the extract date`)}">
        <div class="stat-value">${num(TOTAL)}</div><div class="stat-label">Award records</div></div>
      <div class="stat" data-tip="${tipAttr(`${num(core.totals.awards)} distinct award names. Three names — the National Medal, the Humanitarian Overseas Service Medal and the Group Bravery Citation — are recorded under two systems each, so the chart draws ${num(flat.length)} system-and-award blocks.`)}">
        <div class="stat-value">${num(core.totals.awards)}</div><div class="stat-label">Distinct award names</div></div>
      <div class="stat" data-tip="${tipAttr(`${num(core.totals.systems)} award systems: ${(tree.children ?? []).map((s) => s.name).join(', ')}`)}">
        <div class="stat-value">${num(core.totals.systems)}</div><div class="stat-label">Award systems</div></div>
      <div class="stat" data-tip="${tipAttr(`National Medal: ${num(nationalMedal)} records, ${pct(TOTAL > 0 ? nationalMedal / TOTAL : NaN)} of the register. Awarded for 15 years of diligent service — length of service, not a particular achievement.`)}">
        <div class="stat-value">${pct(TOTAL > 0 ? nationalMedal / TOTAL : NaN)}</div>
        <div class="stat-label">Of the register is the National Medal, a 15-year long-service award</div></div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2>What the register is made of</h2>
        <p class="panel-sub">An <strong>icicle chart</strong>: each column is one level of the hierarchy,
          reading left to right — ${LEVELS.slice(1).map((l) => escapeHtml(l)).join(' → ')}. A block's
          <strong>height</strong> is proportional to how many records it holds, and its children sit
          inside its own vertical extent, so a column always adds up to the column on its left.
          Colour is the award system, and each generation is drawn a shade paler than its parent.
          <strong>Click any block with children to zoom into it</strong>; the trail above zooms back
          out. Click a ${gloss('division', 'division')} block in the last column — or pick one from the
          list under the chart — to trace that division across every award.</p>
      </div>
      <nav class="reg-crumbs" aria-label="Hierarchy trail">${crumbs.join('')}</nav>
      <div class="chart-wrap reg-chart"></div>
      <p class="panel-sub reg-axis">Horizontal axis: depth in the hierarchy (no scale — it is a level,
        not a quantity). Vertical axis: share of the parent block, so the full height of a column equals
        the block it descends from. In practice only the Order of Australia and some Imperial awards
        record a division at all; everything else is published as “Not stated”, which is the register's
        silence rather than a category of recipient.</p>
      <div class="reg-legend" role="group" aria-label="Award systems — click to zoom into one">
        <button type="button" data-goto="" aria-current="${focusDepth === 0 ? 'true' : 'false'}"
          aria-label="Show all systems"
          data-tip="${tipAttr(`All ${num(TOTAL)} records, all ${num(core.totals.systems)} systems`)}">
          <span class="swatch" style="background:var(--bg-active)"></span>
          <span class="reg-name">All systems</span><span class="reg-n">${num(TOTAL)}</span></button>
        ${legend}
      </div>
      <div class="reg-detail" aria-live="polite"></div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2>The twenty largest entries</h2>
        <p class="panel-sub">The same data as a list, for reading rather than scanning. Long-service and
          commemorative medals dominate the top; click a row to zoom the chart to that award.</p>
      </div>
      <div class="scroller">
        <table>
          <thead><tr><th scope="col">Award</th><th scope="col">System</th>
            <th scope="col" class="n">Records</th><th scope="col" class="n">Share of register</th></tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>
    </div>`;

  const wrap = root.querySelector<HTMLElement>('.reg-chart');
  const detail = root.querySelector<HTMLElement>('.reg-detail');

  // ---------------------------------------------------------------- detail
  function renderDetail(): void {
    if (!detail) return;
    if (!selected) {
      const pills = divisions
        .map(
          ([name, n]) =>
            `<button type="button" data-select="${escapeHtml(name)}" aria-pressed="false"
              aria-label="Highlight the ${escapeHtml(name)} division across the chart, ${num(n)} records"
              data-tip="${tipAttr(`${name}\n${num(n)} records across the whole register — ${pct(TOTAL > 0 ? n / TOTAL : NaN)}\nClick to highlight it in the chart`)}">
              <span>${escapeHtml(name)}</span><span class="reg-n">${num(n)}</span></button>`,
        )
        .join('');
      detail.innerHTML = `<p class="panel-sub">Divisions across the whole register. The
        ${gloss('division', 'General and Military divisions')} exist only inside the Order of Australia
        and a handful of Imperial awards; “Not stated” is by far the largest group because most awards
        on the register have no division at all. Pick one to highlight it wherever it appears.</p>
        <div class="reg-divpills">${pills}</div>`;
      return;
    }

    const sel = selected;
    const total = leafTotal(tree, sel);
    const rows = (tree.children ?? [])
      .map((sys) => ({ name: sys.name, n: leafTotal(sys, sel) }))
      .filter((r) => r.n > 0)
      .sort((a, b) => b.n - a.n)
      .map(
        (r) => `<tr data-tip="${tipAttr(`${sel} division, ${r.name} system\n${num(r.n)} records — ${pct(total > 0 ? r.n / total : NaN)} of all “${sel}” records`)}">
          <td><span class="swatch" style="background:${systemColour(r.name)};display:inline-block;vertical-align:-2px"></span>
          ${escapeHtml(r.name)}</td><td class="n">${num(r.n)}</td>
          <td class="n">${pct(total > 0 ? r.n / total : NaN)}</td></tr>`,
      )
      .join('');
    detail.innerHTML = `<p class="panel-sub"><strong>${escapeHtml(sel)}</strong> division —
      ${num(total)} records, ${pct(TOTAL > 0 ? total / TOTAL : NaN)} of the register. It is highlighted
      in the chart; everything else is dimmed.</p>
      <div class="scroller"><table><thead><tr><th scope="col">System</th>
        <th scope="col" class="n">Records</th><th scope="col" class="n">Share of division</th></tr></thead>
        <tbody>${rows}</tbody></table></div>
      <p style="margin-top:var(--space-md)"><button type="button" class="pill" data-clear="1"
        aria-label="Clear the division highlight">Clear highlight</button></p>`;
  }

  // ----------------------------------------------------------------- chart
  let zoom: SvgZoomHandle | null = null;
  let lastW = -1;

  function draw(): void {
    if (!wrap) return;
    const W = Math.max(300, Math.round(wrap.clientWidth || 640));
    lastW = W;
    const narrow = W < 560;
    const fs = narrow ? 11 : 12;
    const headH = 26;
    const footH = 20;
    const plotH = narrow ? 400 : 520;
    const rootW = Math.min(narrow ? 62 : 108, Math.round(W * 0.28));
    const below = depthBelow(focusNode);
    const maxDepth = Math.max(1, Math.min(narrow ? 2 : 3, below));
    const colW = maxDepth > 0 ? (W - rootW) / maxDepth : 0;
    const svgH = headH + plotH + footH;

    const rects = icicleLayout(focusNode, { width: W, height: plotH, maxDepth, rootWidth: rootW });

    const headParts: string[] = [];
    const nodeParts: string[] = [];
    const footParts: string[] = [];

    // Column headers — the depth axis, labelled.
    const headLabel = (text: string, cx: number, w: number): void => {
      const t = fitText(text, w, 11);
      if (!t || (t !== text && t.length < 7)) return;
      headParts.push(
        `<text x="${safe(cx)}" y="16" text-anchor="middle" font-size="11" font-weight="600"
          fill="var(--text-secondary)">${escapeHtml(t)}</text>`,
      );
    };
    headLabel(LEVELS[Math.min(focusDepth, LEVELS.length - 1)], rootW / 2, rootW);
    for (let d = 1; d <= maxDepth; d++) {
      const lvl = LEVELS[Math.min(focusDepth + d, LEVELS.length - 1)];
      headLabel(lvl, rootW + (d - 0.5) * colW, colW);
    }

    for (const r of rects) {
      const absPath = focusPath.concat(r.path);
      const absDepth = absPath.length;
      const node = nodeAt(focusNode, r.path);
      const sysName = absDepth > 0 ? ((tree.children ?? [])[absPath[0]]?.name ?? '') : '';
      const isLeaf = !r.hasChildren && absDepth > 0;

      const fill =
        absDepth === 0
          ? 'var(--bg-elevated)'
          : tint(systemColour(sysName), absDepth === 1 ? 0 : absDepth === 2 ? 0.34 : 0.6);

      // Opacity encodes the current division selection.
      let opacity = 1;
      if (selected) {
        if (isLeaf) opacity = r.name === selected ? 1 : 0.18;
        else if (absDepth === 0) opacity = 1;
        else opacity = node && containsLeaf(node, selected) ? 0.8 : 0.18;
      }

      const shareReg = TOTAL > 0 ? val(r.n) / TOTAL : NaN;
      const shareParent = val(r.parentN) > 0 ? val(r.n) / val(r.parentN) : NaN;
      const parentName =
        r.depth === 0 ? '' : (nodeAt(focusNode, r.path.slice(0, -1))?.name ?? focusNode.name);
      const levelName = LEVELS[Math.min(absDepth, LEVELS.length - 1)];

      let action = '';
      let goto = '';
      if (r.depth === 0) {
        if (focusDepth > 0) {
          action = 'Click to zoom back out';
          goto = focusPath.slice(0, -1).join('.');
        }
      } else if (r.hasChildren) {
        action = 'Click to zoom in';
        goto = absPath.join('.');
      } else {
        action = selected === r.name ? 'Click to clear the highlight' : 'Click to highlight this division everywhere';
      }

      const display = r.depth === 0 && focusDepth === 0 ? 'The register' : r.name;
      const tipLines = [display, levelName, `${num(val(r.n))} records`];
      if (parentName) tipLines.push(`${pct(shareParent)} of ${parentName}`);
      tipLines.push(`${pct(shareReg)} of the register`);
      if (action) tipLines.push(action);

      const interactive = goto !== '' || (r.depth === 0 && focusDepth > 0) || isLeaf;
      const focusable = interactive && r.h >= 4 && r.w >= 8;
      const attrs: string[] = [];
      if (goto !== '' || (r.depth === 0 && focusDepth > 0)) attrs.push(`data-goto="${escapeHtml(goto)}"`);
      if (isLeaf) attrs.push(`data-select="${escapeHtml(r.name)}"`);
      if (focusable) attrs.push('tabindex="0"', 'role="button"');

      const strokeSel = selected && isLeaf && r.name === selected;
      const stroke = strokeSel
        ? ` stroke="var(--accent-secondary)" stroke-width="1.6"`
        : r.h >= 1.5 && r.w >= 2
          ? ` stroke="var(--bg-surface)" stroke-width="0.5"`
          : '';

      nodeParts.push(
        `<rect class="reg-node${interactive ? ' reg-act' : ''}" x="${safe(r.x)}" y="${safe(r.y)}"
          width="${safe(Math.max(0, r.w))}" height="${safe(Math.max(0, r.h))}" fill="${fill}"
          opacity="${opacity}"${stroke} ${attrs.join(' ')}
          aria-label="${escapeHtml(`${display}, ${levelName}, ${num(val(r.n))} records, ${pct(shareReg)} of the register${action ? `. ${action}` : ''}`)}"
          data-tip="${tipAttr(tipLines.join('\n'))}"></rect>`,
      );

      // Labels only where they genuinely fit; the tooltip carries the full text.
      // Each label is clipped to its own column as well as truncated, so a wide
      // glyph run can never print over the column beside it.
      if (r.h >= fs + 5 && r.w >= 44) {
        const clip = ` clip-path="url(#reg-clip-${r.depth})"`;
        const label = fitText(display, r.w, fs);
        // A label stubbed down to two or three characters is noise, not a
        // label; the column header and the tooltip carry the meaning instead.
        if (label && (label === display || label.length >= 7)) {
          const light = absDepth <= 1 && absDepth > 0;
          const colour = light ? '#ffffff' : 'var(--text-primary)';
          nodeParts.push(
            `<text x="${safe(r.x + 6)}" y="${safe(r.y + fs + 1)}" font-size="${fs}" fill="${colour}"
              opacity="${opacity}" pointer-events="none"${clip}>${escapeHtml(label)}</text>`,
          );
          if (r.h >= fs * 2 + 10) {
            const subFull = `${num(val(r.n))} · ${pct(shareReg)}`;
            const sub = fitText(subFull, r.w, fs - 1);
            if (sub === subFull) {
              nodeParts.push(
                `<text x="${safe(r.x + 6)}" y="${safe(r.y + fs * 2 + 3)}" font-size="${fs - 1}"
                  fill="${colour}" opacity="${safe(opacity * (light ? 0.85 : 0.75))}"
                  pointer-events="none"${clip}>${escapeHtml(sub)}</text>`,
              );
            }
          }
        }
      }
    }

    // Axis labels. The plot sits in its own translated group, so these are
    // written in absolute svg coordinates.
    footParts.push(
      `<text x="0" y="${safe(headH + plotH + 15)}" font-size="11"
        fill="var(--text-tertiary)">Depth of the hierarchy →</text>`,
      `<text x="${safe(W)}" y="${safe(headH + plotH + 15)}" font-size="11" text-anchor="end"
        fill="var(--text-tertiary)">Block height ∝ records</text>`,
    );

    zoom?.destroy();
    zoom = null;
    wrap.innerHTML = '';
    const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgEl.setAttribute('class', 'chart');
    svgEl.setAttribute('role', 'group');
    svgEl.setAttribute('width', '100%');
    svgEl.setAttribute('height', String(svgH));
    svgEl.setAttribute('viewBox', `0 0 ${W} ${svgH}`);
    svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svgEl.setAttribute(
      'aria-label',
      `Icicle chart of the honours register: ${focusDepth === 0 ? 'all systems' : focusNode.name}, ${num(val(focusNode.n))} records, broken down by ${LEVELS.slice(Math.min(focusDepth + 1, LEVELS.length - 1)).join(' then ')}`,
    );
    const clips: string[] = [
      `<clipPath id="reg-clip-0"><rect x="0" y="0" width="${safe(rootW)}" height="${safe(plotH)}"></rect></clipPath>`,
    ];
    for (let d = 1; d <= maxDepth; d++) {
      clips.push(
        `<clipPath id="reg-clip-${d}"><rect x="${safe(rootW + (d - 1) * colW)}" y="0"
          width="${safe(colW)}" height="${safe(plotH)}"></rect></clipPath>`,
      );
    }

    svgEl.innerHTML = `<defs>${clips.join('')}</defs>
      <g class="reg-head">${headParts.join('')}</g>
      <g transform="translate(0,${headH})">${nodeParts.join('')}</g>
      <g class="reg-foot">${footParts.join('')}</g>`;
    wrap.appendChild(svgEl);
    zoom = attachSvgZoom(svgEl, { maxScale: 12 });
  }

  // --------------------------------------------------------------- events
  function act(el: Element): boolean {
    const goto = el.getAttribute('data-goto');
    if (goto !== null) {
      if (goto) ctx.go('register', { p: goto });
      else ctx.go('register');
      return true;
    }
    const sel = el.getAttribute('data-select');
    if (sel !== null) {
      selected = selected === sel ? null : sel;
      renderDetail();
      draw();
      return true;
    }
    if (el.getAttribute('data-clear') !== null) {
      selected = null;
      renderDetail();
      draw();
      return true;
    }
    return false;
  }

  const onClick = (e: Event): void => {
    const el = (e.target as Element | null)?.closest?.('[data-goto],[data-select],[data-clear]');
    if (el) act(el);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = (e.target as Element | null)?.closest?.('[data-goto],[data-select],[data-clear]');
    if (el && el.tagName.toLowerCase() === 'rect') {
      e.preventDefault();
      act(el);
    }
  };
  root.addEventListener('click', onClick);
  root.addEventListener('keydown', onKey);

  // Redraw only when the available WIDTH actually changes, so the observer can
  // never chase its own height changes into a loop.
  let pending = 0;
  const ro = new ResizeObserver(() => {
    if (!wrap) return;
    const w = Math.max(300, Math.round(wrap.clientWidth || 640));
    if (w === lastW) return;
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => {
      pending = 0;
      draw();
    });
  });
  if (wrap) ro.observe(wrap);

  renderDetail();
  draw();

  return () => {
    if (pending) cancelAnimationFrame(pending);
    ro.disconnect();
    zoom?.destroy();
    zoom = null;
    root.removeEventListener('click', onClick);
    root.removeEventListener('keydown', onKey);
  };
};
